import type { ActivationFlowDefinition } from "../types";

export const firstExportCompositeFlow: ActivationFlowDefinition = {
    id: "first_export_composite",
    title: "Separate Speakers Workflow",
    steps: [
        {
            id: "intro_editor",
            title: "Separate a two-person recording",
            target: "[data-tour='editor-workspace-area']",
            placement: "center",
            spotlightPadding: 14,
            content:
                "In this walkthrough, we'll split a two-person grid recording into separate tracks and build a sequence.",
            advance: { type: "manual" },
        },
        {
            id: "assign_speakers_transcript",
            title: "Name transcript speakers",
            target: "[data-tour='transcript-panel']",
            placement: "right",
            spotlightPadding: 12,
            content:
                "In the transcript, assign speaker names so there are exactly two assigned speakers. Use the speaker tags to map all diarization colors.",
            advance: { type: "manual" },
        },
        {
            id: "open_separate_speakers_tool",
            title: "Start Separate speakers",
            target: "[data-tour='ai-tools-separate-speakers']",
            placement: "left",
            content: "Click Separate speakers to open the guided split workflow.",
            preStepAction: "open_ai_tools_sidebar",
            advance: {
                type: "click",
                selector: "[data-tour='ai-tools-separate-speakers']",
            },
        },
        {
            id: "continue_after_assignment",
            title: "Continue when ready",
            target: "[data-tour='decompose-continue-button']",
            placement: "left",
            content:
                "When both speakers are assigned, click Continue. If disabled, finish speaker assignment first.",
            advance: {
                type: "click",
                selector: "[data-tour='decompose-continue-button']",
            },
        },
        {
            id: "crop_both_speakers",
            title: "Crop both speakers",
            target: "[data-tour='editor-canvas-workspace']",
            placement: "left",
            spotlightPadding: 18,
            content:
                "Crop in canvas using the toolbar above it: first the person on the right, then the person on the left. Click Done after each crop.",
            advance: { type: "condition", condition: "decompose_export_started" },
        },
        {
            id: "wait_for_processing",
            title: "Processing speaker tracks",
            target: "[data-tour='editor-open-sidebar-column']",
            placement: "left",
            content:
                "Watch progress here. The tour continues when processing is finished; upload can continue in the background.",
            preStepAction: "open_decompose_sidebar",
            advance: { type: "condition", condition: "decompose_processing_complete" },
        },
        {
            id: "review_project_tracks",
            title: "Separate tracks are ready",
            target: "[data-tour='project-files-panel']",
            placement: "right",
            content:
                "Project is opened for you. Select both separated tracks using Cmd/Ctrl + click (hold Cmd on Mac or Ctrl on Windows while clicking each track).",
            preStepAction: "open_project_sidebar",
            advance: { type: "condition", condition: "create_sequence_action_visible" },
        },
        {
            id: "create_sequence_from_selection",
            title: "Create sequence",
            target: "[data-tour='asset-popover-create-sequence']",
            placement: "left",
            content: "Click Create Sequence.",
            advance: {
                type: "click",
                selector: "[data-tour='asset-popover-create-sequence']",
            },
        },
        {
            id: "create_new_composition",
            title: "Create a new composition",
            target: "[data-tour='project-compositions-add']",
            placement: "right",
            content:
                "In Compositions, click + to create a new composition. It will open automatically.",
            preStepAction: "open_project_sidebar",
            advance: {
                type: "click",
                selector: "[data-tour='project-compositions-add']",
            },
        },
        {
            id: "open_sequences_group",
            title: "Open sequences",
            target: "[data-tour='project-sequences-toggle']",
            placement: "right",
            content: "Expand Sequences.",
            preStepAction: "open_project_sidebar",
            advance: {
                type: "click",
                selector: "[data-tour='project-sequences-toggle']",
            },
        },
        {
            id: "open_sequence_actions",
            title: "Open created sequence",
            target: "[data-tour='project-sequence-row']",
            placement: "right",
            content: "Click the sequence you just created.",
            preStepAction: "open_project_sidebar",
            advance: {
                type: "click",
                selector: "[data-tour='project-sequence-row']",
            },
        },
        {
            id: "insert_sequence_into_script",
            title: "Insert into script",
            target: "[data-tour='sequence-popover-insert-script']",
            placement: "left",
            content: "Click Insert into script.",
            preStepAction: "open_project_sidebar",
            advance: {
                type: "click",
                selector: "[data-tour='sequence-popover-insert-script']",
            },
        },
        {
            id: "open_aspect_ratio",
            title: "Open aspect ratio",
            target: "[data-tour='editor-aspect-ratio-button']",
            placement: "right",
            content: "Click the aspect-ratio button in the top-left of the canvas.",
            advance: {
                type: "click",
                selector: "[data-tour='editor-aspect-ratio-button']",
            },
        },
        {
            id: "set_vertical_ratio",
            title: "Switch to 9:16",
            target: "[data-tour='clip-size-option-9-16']",
            placement: "right",
            content: "Choose 9:16.",
            advance: {
                type: "click",
                selector: "[data-tour='clip-size-option-9-16']",
            },
        },
        {
            id: "adjust_multicam_in_canvas",
            title: "Adjust speaker layout",
            target: "[data-tour='editor-canvas-workspace']",
            placement: "left",
            spotlightPadding: 18,
            content:
                "Click in canvas and move the visual elements to see that each speaker is now a separate track.",
            advance: { type: "manual" },
        },
        {
            id: "open_export_menu",
            title: "Open export",
            target: "[data-tour='editor-export-button']",
            placement: "bottom",
            content: "Open Export.",
            advance: {
                type: "click",
                selector: "[data-tour='editor-export-button']",
            },
        },
        {
            id: "start_export",
            title: "Export final result",
            target: "[data-tour='export-card-submit']",
            placement: "left",
            content: "Click Export to finish.",
            advance: {
                type: "click",
                selector: "[data-tour='export-card-submit']",
            },
        },
    ],
};
